// AdminViewContext — Desktop/Mobile preference for the admin shell (Track 9).
// Port of legacy `app/admin/_components/admin-view-context.tsx`:
// - `isDesktopView` reflects either the actual viewport OR the user's saved
//   preference (when the device is wide enough to override).
// - `setViewMode` persists the choice in localStorage so the next session
//   starts in the chosen mode.
// - `isMobileDevice` is computed from `window.innerWidth < 1024`; on mobile
//   devices the toggle hides itself and `setViewMode` becomes a no-op.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

export type AdminRole = "admin" | "support" | "viewer";

interface AdminViewContextType {
  readonly isDesktopView: boolean;
  readonly setViewMode: (mode: "desktop" | "mobile") => void;
  readonly isMobileDevice: boolean;
  readonly role: AdminRole;
}

const STORAGE_KEY = "quartel_admin_view_mode";
const DESKTOP_BREAKPOINT_PX = 1024;

const AdminViewContextInternal = createContext<
  AdminViewContextType | undefined
>(undefined);

interface AdminViewProviderProps {
  readonly children: React.ReactNode;
  readonly role: AdminRole;
}

export function AdminViewProvider({
  children,
  role,
}: AdminViewProviderProps): React.JSX.Element {
  const [isDesktopDevice, setIsDesktopDevice] = useState(false);
  const [viewMode, setViewModeState] = useState<"desktop" | "mobile">("mobile");
  const preferenceRef = useRef<"desktop" | "mobile" | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "desktop" || stored === "mobile") {
      preferenceRef.current = stored;
      setViewModeState(stored);
    }

    function updateDevice(): void {
      const desktop = window.innerWidth >= DESKTOP_BREAKPOINT_PX;
      setIsDesktopDevice(desktop);
      if (!desktop) {
        setViewModeState("mobile");
        return;
      }
      if (!preferenceRef.current) {
        setViewModeState("desktop");
      }
    }
    updateDevice();
    window.addEventListener("resize", updateDevice);
    return () => window.removeEventListener("resize", updateDevice);
  }, []);

  const setViewMode = useCallback((mode: "desktop" | "mobile"): void => {
    preferenceRef.current = mode;
    setViewModeState(mode);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, mode);
    }
  }, []);

  const isDesktopView = viewMode === "desktop";
  const isMobileDevice = !isDesktopDevice;

  const value = useMemo<AdminViewContextType>(
    () => ({ isDesktopView, setViewMode, isMobileDevice, role }),
    [isDesktopView, setViewMode, isMobileDevice, role],
  );

  return (
    <AdminViewContextInternal.Provider value={value}>
      {children}
    </AdminViewContextInternal.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- hook + provider colocated by design (legacy parity port)
export function useAdminView(): AdminViewContextType {
  const context = useContext(AdminViewContextInternal);
  if (context === undefined) {
    throw new Error("useAdminView must be used within an AdminViewProvider");
  }
  return context;
}

// eslint-disable-next-line react-refresh/only-export-components -- hook + provider colocated by design (legacy parity port)
export function useAdminViewRole(defaultRole: AdminRole): AdminRole {
  return useContext(AdminViewContextInternal)?.role ?? defaultRole;
}
